import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, CreditCard, Shield } from 'lucide-react';

const PLAN_INFO = {
    pro: { name: 'Pro Plan', price: '1 ₺' },
    premium: { name: 'Premium Plan', price: '1 ₺' },
};

const Checkout = () => {
    const { userData, tenantData, effectiveTenantId } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const formRef = useRef(null);

    const targetPlan = searchParams.get('plan') || 'pro';
    const planInfo = PLAN_INFO[targetPlan] || PLAN_INFO.pro;

    useEffect(() => {
        if (!effectiveTenantId || !userData) return;

        // Already actively subscribed to same/higher plan
        const planOrder = { free: 0, pro: 1, premium: 2 };
        const alreadySubscribed = tenantData?.subscription_status === 'ACTIVE' &&
            (planOrder[tenantData?.plan] || 0) >= (planOrder[targetPlan] || 0);
        if (alreadySubscribed) {
            navigate('/app', { replace: true });
            return;
        }

        const initCheckout = async () => {
            try {
                const res = await fetch('/api/subscription?action=create', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        tenantId: effectiveTenantId,
                        email: userData.email,
                        firstName: userData.firstName,
                        lastName: userData.lastName,
                        phone: userData.phone || '',
                        plan: targetPlan,
                    })
                });

                const data = await res.json();

                if (!res.ok) {
                    setError(data.error || 'Ödeme başlatılamadı');
                    setLoading(false);
                    return;
                }

                // Inject iyzico checkout form
                if (formRef.current && data.checkoutFormContent) {
                    formRef.current.innerHTML = data.checkoutFormContent;
                    const scripts = formRef.current.querySelectorAll('script');
                    scripts.forEach(script => {
                        const newScript = document.createElement('script');
                        if (script.src) {
                            newScript.src = script.src;
                        } else {
                            newScript.textContent = script.textContent;
                        }
                        document.body.appendChild(newScript);
                    });
                }
                setLoading(false);
            } catch (err) {
                console.error('Checkout error:', err);
                setError('Ödeme sistemi yüklenemedi');
                setLoading(false);
            }
        };

        initCheckout();
    }, [effectiveTenantId, userData, tenantData, targetPlan]);

    return (
        <div style={{ minHeight: '100vh', background: 'var(--bg-main)', padding: '2rem' }}>
            <div style={{ maxWidth: '640px', margin: '0 auto' }}>
                <button
                    onClick={() => navigate(-1)}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', marginBottom: '2rem', fontSize: '0.875rem', fontWeight: 600 }}
                >
                    <ArrowLeft size={18} /> Geri Dön
                </button>

                <div className="card" style={{ padding: '2.5rem', borderRadius: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '2rem' }}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(var(--primary-rgb), 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CreditCard size={24} color="var(--primary)" />
                        </div>
                        <div>
                            <h1 style={{ fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>{planInfo.name} Aboneliği</h1>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 0 }}>Her ay {planInfo.price} — istediğiniz zaman iptal edebilirsiniz</p>
                        </div>
                    </div>

                    {error && (
                        <div style={{ background: '#fff1f2', color: '#e11d48', padding: '1rem', borderRadius: '12px', marginBottom: '1.5rem', fontSize: '0.875rem', textAlign: 'center' }}>
                            {error}
                        </div>
                    )}

                    {loading && (
                        <div style={{ textAlign: 'center', padding: '3rem' }}>
                            <div className="animate-spin" style={{ width: '32px', height: '32px', border: '3px solid rgba(111, 78, 55, 0.2)', borderTopColor: 'var(--primary)', borderRadius: '50%', margin: '0 auto 1rem' }}></div>
                            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Ödeme formu yükleniyor...</p>
                        </div>
                    )}

                    <div ref={formRef} id="iyzipay-checkout-form" />

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2rem', padding: '1rem', background: 'rgba(16, 185, 129, 0.05)', borderRadius: '12px' }}>
                        <Shield size={18} color="#10b981" />
                        <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                            Ödemeler iyzico güvenlik altyapısı ile korunmaktadır.
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Checkout;
